import { DropdownOptionType } from '@ardoq/dropdown-menu';
import type { DropdownCustomContentOption } from '@ardoq/dropdown-menu';
import { Box } from '@ardoq/layout';
import type { SelectOptionsOrGroups, SelectOption } from '@ardoq/select';
import { type I18nLocale } from '@ardoq/i18n';
import { useEffect, useMemo, useState } from 'react';
import {
  flattenOptionKeys,
  toComponentMatrix2AxisDropdownContentViewProps,
  type DropdownLocalState,
} from './toComponentMatrix2AxisDropdownProps';
import { buildSourceSelectComponents } from './ComponentMatrix2AxisDropdownSelectComponents';
import { ComponentMatrix2AxisDropdownContentView } from './ComponentMatrix2AxisDropdownContentView';
import { getAxisSourceKey } from './utils';
import { Axis, AxisSortOrder, AxisSourceValue } from './types';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';

type ComponentMatrixAxisDropdownContentProps = {
  axis: Axis;
  label: string;
  sourceValue: AxisSourceValue | null;
  sourceOptions: SelectOptionsOrGroups<AxisSourceValue>;
  onSourceChange: (value: AxisSourceValue | null) => void;
  sortingValue: AxisSortOrder | null;
  isSortingDisabled: boolean;
  sortingOptions: SelectOption<AxisSortOrder>[];
  onSortingChange: (value: AxisSortOrder) => void;
  i18nLocale: I18nLocale;
  value: string | null;
  onValueChange: (value: string | null) => void;
  additionalHeaderFields: FieldWithPermissions[];
  additionalHeaderFieldNames: string[];
  onAdditionalHeaderFieldNamesChange: (fieldNames: string[]) => void;
};

const useAutoDeselectUnavailableSourceValue = ({
  sourceValue,
  sourceOptions,
  onSourceChange,
}: Pick<
  ComponentMatrixAxisDropdownContentProps,
  'sourceValue' | 'sourceOptions' | 'onSourceChange'
>) => {
  const availableSourceKeys = useMemo(
    () => flattenOptionKeys(sourceOptions),
    [sourceOptions]
  );

  useEffect(() => {
    if (sourceValue === null) return;
    // Skip while options haven't loaded yet — avoids destroying a persisted
    // selection (and its sortOrder) before the data arrives.
    if (availableSourceKeys.length === 0) return;
    if (!availableSourceKeys.includes(getAxisSourceKey(sourceValue))) {
      onSourceChange(null);
    }
  }, [sourceValue, availableSourceKeys, onSourceChange]);
};

const useComponentMatrixAxisDropdown = ({
  axis,
  i18nLocale,
  sourceValue,
  sourceOptions,
  onSourceChange,
}: Pick<
  ComponentMatrixAxisDropdownContentProps,
  'axis' | 'i18nLocale' | 'sourceValue' | 'sourceOptions' | 'onSourceChange'
>): DropdownLocalState => {
  const [isSourceMenuOpen, setIsSourceMenuOpen] = useState(false);

  useAutoDeselectUnavailableSourceValue({
    sourceValue,
    sourceOptions,
    onSourceChange,
  });

  const sourceSelectComponents = useMemo(() => {
    const handleDeselect = () => {
      onSourceChange(null);
      setIsSourceMenuOpen(false);
    };

    return buildSourceSelectComponents({
      axis,
      i18nLocale,
      onDeselect: handleDeselect,
    });
  }, [axis, i18nLocale, onSourceChange]);

  return { isSourceMenuOpen, setIsSourceMenuOpen, sourceSelectComponents };
};

const ComponentMatrix2AxisDropdownContent = (
  props: ComponentMatrixAxisDropdownContentProps
) => {
  const localState = useComponentMatrixAxisDropdown(props);
  return (
    <ComponentMatrix2AxisDropdownContentView
      {...toComponentMatrix2AxisDropdownContentViewProps(props, localState)}
    />
  );
};

const buildAxisDropdownOption = (
  props: ComponentMatrixAxisDropdownContentProps
): DropdownCustomContentOption => ({
  type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
  content: (
    <Box style={{ width: '370px' }}>
      <ComponentMatrix2AxisDropdownContent {...props} />
    </Box>
  ),
});

export {
  buildAxisDropdownOption,
  type ComponentMatrixAxisDropdownContentProps,
};
